/**
 * Constants for "Do You Like This Work?" application
 * Includes reason codes, categories, and display mappings
 */
// =============================================================================
// AI Status Display
// =============================================================================
export const AI_STATUS_DISPLAY = {
    none: {
        label_en: 'Human-Made',
        label_ja: '人間制作',
        description_en: 'Created entirely by a human artist without AI assistance',
        description_ja: 'AIの支援なしに人間のアーティストが完全に制作',
        color: 'green',
    },
    partial: {
        label_en: 'AI-Assisted',
        label_ja: 'AIアシスト',
        description_en: null, // Uses ai_notes for specifics
        description_ja: null,
        color: 'yellow',
    },
    full: {
        label_en: 'AI-Generated',
        label_ja: 'AI生成',
        description_en: 'Created primarily or entirely using AI image generation',
        description_ja: '主にまたは完全にAI画像生成を使用して作成',
        color: 'blue',
    },
};
// =============================================================================
// Vote Reason Codes
// =============================================================================
export const YES_REASONS = {
    positive_emotion: {
        en: 'It evokes a positive emotion',
        ja: 'ポジティブな感情を呼び起こす',
    },
    hobby_related: {
        en: 'It pertains to hobbies I enjoy',
        ja: '自分の趣味に関連している',
    },
    like_subject: {
        en: 'I like the subject(s) presented',
        ja: '描かれている対象が好き',
    },
    looks_human_soul: {
        en: 'It looks human-made / has "soul"',
        ja: '人間が作ったように見える・「魂」を感じる',
    },
    ai_sensible: {
        en: 'It looks like AI but used sensibly',
        ja: 'AIっぽいが、うまく使われている',
    },
};
export const NO_REASONS = {
    negative_emotion: {
        en: 'It evokes a negative emotion',
        ja: 'ネガティブな感情を呼び起こす',
    },
    hobby_unrelated: {
        en: "It pertains to hobbies I don't enjoy",
        ja: '自分の趣味に関係ない',
    },
    dislike_subject: {
        en: "I don't like the subject(s) presented",
        ja: '描かれている対象が好きではない',
    },
    looks_ai_slop: {
        en: 'It looks like "AI slop"',
        ja: '「AI生成っぽい」と感じる',
    },
    human_unsettling: {
        en: 'It looks human-made but unsettling',
        ja: '人間が作ったようだが不気味',
    },
};
// =============================================================================
// Categories
// =============================================================================
export const CATEGORIES = {
    landscapes: {
        en: 'Landscapes',
        ja: '風景',
        description: 'Natural or urban scenery',
    },
    portraits: {
        en: 'Portraits',
        ja: '肖像画',
        description: "Focus on a single person/character's face",
    },
    people: {
        en: 'People',
        ja: '人物',
        description: 'Humans in scenes (not portrait-focused)',
    },
    classical: {
        en: 'Classical / Historic',
        ja: '古典・歴史',
        description: 'Historical art and vintage photography',
    },
    modern_photo: {
        en: 'Modern Photography',
        ja: '現代写真',
        description: 'Contemporary photography',
    },
    abstract: {
        en: 'Abstract',
        ja: '抽象',
        description: 'Non-representational art',
    },
    animals: {
        en: 'Animals',
        ja: '動物',
        description: 'Wildlife and pets',
    },
    fantasy_scifi: {
        en: 'Fantasy / Sci-Fi',
        ja: 'ファンタジー・SF',
        description: 'Imaginative/speculative content',
    },
    still_life: {
        en: 'Still Life',
        ja: '静物',
        description: 'Objects and arrangements',
    },
};
// =============================================================================
// License Types
// =============================================================================
export const LICENSES = {
    'CC0-Public-Domain': 'Public Domain / CC0',
    'CC-BY-4.0': 'Creative Commons Attribution 4.0',
    'CC-BY-SA-4.0': 'Creative Commons Attribution-ShareAlike 4.0',
    'Unsplash-License': 'Unsplash License',
    'Pexels-License': 'Pexels License',
    'Pixabay-License': 'Pixabay License',
    'CreativeML-OpenRAIL-M': 'CreativeML OpenRAIL-M (Stable Diffusion)',
    'OpenAI-ToS': 'OpenAI Terms of Service (DALL-E)',
    'Midjourney-ToS': 'Midjourney Terms of Service',
    'Google-ToS': 'Google Terms of Service (Imagen)',
    'Artist-Permission': 'Direct Artist Permission',
    'Artist-Statement': 'Artist Public Statement',
    'Contact-Artist': 'Pending Artist Permission',
};
export const CONTRADICTION_RULES = [
    {
        type: 'false_positive_ai',
        condition: (aiStatus, reasons) => aiStatus === 'none' && reasons.includes('looks_ai_slop'),
        message_en: 'This image was entirely human-made, but you thought it looked like AI!',
        message_ja: 'この画像は完全に人間が作ったものですが、AIっぽいと感じましたね！',
    },
    {
        type: 'false_negative_ai',
        condition: (aiStatus, reasons) => aiStatus === 'full' && reasons.includes('looks_human_soul'),
        message_en: 'This image was fully AI-generated, but you thought it had human "soul"!',
        message_ja: 'この画像は完全にAI生成ですが、人間の「魂」を感じましたね！',
    },
    {
        type: 'partial_surprise',
        condition: (aiStatus, reasons) => aiStatus === 'partial' &&
            (reasons.includes('looks_ai_slop') || reasons.includes('looks_human_soul')),
        message_en: 'This image was AI-assisted with significant human input - a collaborative effort!',
        message_ja: 'この画像はAIアシストで、人間の大幅な入力がありました - 共同作業です！',
    },
];
// =============================================================================
// UI Constants
// =============================================================================
export const COLORS = {
    // Grayscale
    black: '#000000',
    'gray-900': '#1a1a1a',
    'gray-600': '#666666',
    'gray-200': '#e5e5e5',
    'gray-100': '#f5f5f5',
    white: '#ffffff',
    // Accent
    accent: '#f97316',
    'accent-dark': '#ea580c',
    'accent-light': '#fdba74',
    // Vote buttons (exceptions to palette)
    'vote-yes': '#22c55e',
    'vote-no': '#ef4444',
};
// =============================================================================
// Timing Thresholds
// =============================================================================
export const TIMING = {
    /** Votes under this threshold are considered "snap judgments" */
    SNAP_JUDGMENT_MS: 2000,
    /** Votes over this threshold are considered "deliberate" */
    DELIBERATE_VOTE_MS: 10000,
    /** Maximum time we'll record (to filter outliers) */
    MAX_VOTE_TIME_MS: 300000, // 5 minutes
};
//# sourceMappingURL=constants.js.map